import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { StdtInfo, getAttendanceByStudentAndYear, getSchoolYear } from '../../Api'
import {
    FiArrowLeft, FiUser, FiUsers, FiCalendar, FiAlertCircle,
    FiCheckSquare, FiFileText, FiBarChart2, FiChevronRight,
    FiX, FiClipboard, FiBookOpen
} from 'react-icons/fi'

import {
    Page,
    TopBar,
    BackButton,
    TopBarTitle,
    Hero,
    HeroInner,
    HeroAvatar,
    HeroInfo,
    HeroName,
    HeroSub,
    HeroClassInfo,
    HeroClassLine,
    StatusBadge,
    Content,
    Grid,
    ActionsSection,
    ActionsTitle,
    ActionGrid,
    ActionButton,
    ActionIcon,
    ActionLabel,
    Card,
    EmptyBox,
    EmptyIcon,
    SkeletonBlock,
    SkeletonLine,
    ModalOverlay,
    ModalBox,
    ModalHeader,
    ModalTitle,
    ModalClose,
    YearNav,
   // YearLabel,
    Legend,
    LegendItem,
    LegendDot,
    SummaryRow,
    SummaryCard,
    SummaryValue,
    SummaryLabel,
    MonthsGrid,
    MonthCard,
    MonthName,
    WeekHeader,
    DaysGrid,
    DayCell,
    ModalBody,
    ModalLoading,
} from './style'

import ReportCardModal from '../../components/ModalBoletins'

import ActivitiesModal from '../../components/ModalActivitys'

import IndividualReportsModal from '../../components/ModalRelatorios'

import PerformanceSection from '../../components/ModalDesempenho'

import { AvisoRecuperacao } from '../../components/AvisoRecuperacao'

import CalendarioEscolar from '../../components/CalendarioEscolar'

import { ModalOcorrenciasPais, AvisoOcorrencias, useOcorrenciasPendentes } from '../../components/Ocorrencias/Pais'

const initials = (n) => (n || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0] || '')
    .join('')
    .toUpperCase()

const statusLabel = (s) => {
    switch ((s || '').toLowerCase()) {
        case 'ativo': return 'Ativo'
        case 'transferido': return 'Transferido'
        case 'inativo': return 'Inativo'
        case 'concluido': return 'Concluído'
        default: return s || '—'
    }
}

// formata data "YYYY-MM-DD" sem deslocamento de fuso (padrão do sistema)
/*const formatDate = (value) => {
    const v = (value || '').trim()
    if (!v) return '—'
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
    return v
}*/

const val = (v) => {
    const t = (v == null ? '' : String(v)).trim()
    return t || '—'
}

const schoolName = (student) => {
    const s = student?.id_school
    if (s && typeof s === 'object') return (s.name || '').trim()
    return ''
}

// ── turma atual do aluno ──
// Regras:
//  1) filtra as turmas pelo ano letivo (mais recente disponível ou o do aluno)
//  2) o aluno precisa estar em id_student da turma
//  3) o aluno NÃO pode estar em transferStudents da turma
const currentClassOf = (student) => {
    const classes = Array.isArray(student?.id_class) ? student.id_class : []
    if (!classes.length) return null

    const sid = String(student?._id || '')

    // extrai o id de um item (string ou objeto {_id})
    const idOf = (v) => {
        if (!v) return ''
        if (typeof v === 'object') return String(v._id || v.id || '')
        return String(v)
    }

    // turmas em que o aluno está matriculado e não foi transferido
    const eligible = classes.filter((c) => {
        if (!c || typeof c !== 'object') return false
        const students = Array.isArray(c.id_student) ? c.id_student.map(idOf) : []
        const transferred = Array.isArray(c.transferStudents) ? c.transferStudents.map(idOf) : []
        return students.includes(sid) && !transferred.includes(sid)
    })

    if (!eligible.length) return null

    // dentre as elegíveis, escolhe a do maior ano (turma atual)
    return eligible.reduce((best, c) => {
        const y = parseInt(c?.year, 10) || 0
        const by = parseInt(best?.year, 10) || 0
        return y > by ? c : best
    }, eligible[0])
}

// nome da turma atual
const currentClassName = (student) => {
    const c = currentClassOf(student)
    return (c?.name || '').trim()
}

// série da turma atual (ex.: "3º ANO"); junta o nível quando existir
const currentClassSerie = (student) => {
    const c = currentClassOf(student)
    if (!c) return ''
    const serie = (c.serie || '').trim()
    const level = (c.level || '').trim()
    if (serie && level) return `${serie} - ${level}`
    return serie || level
}

// nome da professora regente da turma atual
// (espera classRegentTeacher populado com objeto { name } no backend)
const regentTeacherName = (student) => {
    const c = currentClassOf(student)
    if (!c) return ''
    const regents = Array.isArray(c.classRegentTeacher) ? c.classRegentTeacher : []
    for (const t of regents) {
        if (t && typeof t === 'object') {
            const n = (t.name || '').trim()
            if (n) return n
        }
    }
    return ''
}

// formato de avaliação da escola: 'concept' ou 'grade'
const assessmentFormatOf = (student) => {
    const s = student?.id_school
    if (s && typeof s === 'object') return (s.assessmentFormat || '').trim().toLowerCase()
    return ''
}

// ── frequência ──
const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

// normaliza o status para uma das 4 categorias
const normStatus = (s) => {
    const v = (s == null ? '' : String(s)).trim().toUpperCase()
    if (v === 'P') return 'P'
    if (v === 'F') return 'F'
    if (v === 'FJ') return 'FJ'
    return '' // '-' ou vazio => sem marcação
}

// extrai {year, month(1-12), day} de um registro, de forma defensiva:
// usa `date` se existir; senão cai para day/month/year (month pode ser
// número, string ou objeto {value})
const extractYMD = (rec) => {
    if (rec?.date) {
        const d = new Date(rec.date)
        if (!isNaN(d.getTime())) {
            return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
        }
    }
    const y = parseInt(rec?.year, 10)
    let m = rec?.month
    if (m && typeof m === 'object') m = m.value ?? m.month ?? m.number
    m = parseInt(m, 10)
    const day = parseInt(rec?.day, 10)
    if (!y || !m || !day) return null
    return { year: y, month: m, day }
}

// monta um mapa "MM-DD" => status (primeiro registro do dia vence)
const buildDayMap = (records) => {
    const map = {}
    for (const rec of (records || [])) {
        const ymd = extractYMD(rec)
        if (!ymd) continue
        const st = normStatus(rec?.status)
        if (!st) continue
        const key = `${String(ymd.month).padStart(2, '0')}-${String(ymd.day).padStart(2, '0')}`
        if (!(key in map)) map[key] = st // mantém o primeiro do dia
    }
    return map
}

// renderiza um mês (mini-calendário) com os dias marcados
const MonthCalendar = ({ monthIndex, year, dayMap, MonthCard, MonthName, WeekHeader, DaysGrid, DayCell }) => {
    const first = new Date(year, monthIndex, 1)
    const startWeekday = first.getDay() // 0=domingo
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()

    const cells = []
    for (let i = 0; i < startWeekday; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)

    const mm = String(monthIndex + 1).padStart(2, '0')

    return (
        <MonthCard>
            <MonthName>{MESES[monthIndex]}</MonthName>
            <WeekHeader>
                {WEEKDAYS.map((w, i) => <span key={i}>{w}</span>)}
            </WeekHeader>
            <DaysGrid>
                {cells.map((d, i) => {
                    if (d === null) return <DayCell key={i} $empty />
                    const key = `${mm}-${String(d).padStart(2, '0')}`
                    const status = dayMap[key] || ''
                    const title = status
                        ? `${d}/${mm}/${year} — ${status === 'P' ? 'Presente' : status === 'F' ? 'Falta' : 'Falta justificada'}`
                        : `${d}/${mm}/${year}`
                    return (
                        <DayCell key={i} $status={status} title={title}>
                            {d}
                        </DayCell>
                    )
                })}
            </DaysGrid>
        </MonthCard>
    )
}

const ResponsibleStudentDashboard = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()

    // id vem pelo state (useLocation) e, como fallback, pela URL
    const studentId = location?.state?.studentId || params?.id_student || params?.id

    const [student, setStudent] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const cancelled = useRef(false)

    // ── modal de frequência ──
    const [attOpen, setAttOpen] = useState(false)
    const [attYear, setAttYear] = useState(new Date().getFullYear())
    // ano letivo atual da escola — teto do seletor de anos
    const [anoLetivoAtual, setAnoLetivoAtual] = useState(new Date().getFullYear())
    const [attRecords, setAttRecords] = useState([])
    const [attLoading, setAttLoading] = useState(false)
    const attCancelled = useRef(false)

    const [actOpen, setActOpen] = useState(false)

    const [repOpen, setRepOpen] = useState(false)

    // resolve o id da escola (aluno ou sessionStorage) p/ buscar o ano letivo
    const resolveSchoolId = () => {
        const s = student?.id_school
        if (s && typeof s === 'object' && s._id) return s._id
        if (s) return s
        try {
            const fromSession = JSON.parse(sessionStorage.getItem('id-school'))
            if (fromSession) return fromSession
        } catch (e) { /* ignora */ }
        return null
    }

    const openAttendance = () => {
        // ano letivo da escola (backend); fallback = ano atual
        const sid = resolveSchoolId()
        if (sid) {
            getSchoolYear(sid)
                .then((res) => {
                    const y = parseInt(res?.data?.data, 10)
                    const anoValido = !isNaN(y) ? y : new Date().getFullYear()
                    setAttYear(anoValido)
                    setAnoLetivoAtual(anoValido) // teto do seletor
                })
                .catch(() => {
                    setAttYear(new Date().getFullYear())
                    setAnoLetivoAtual(new Date().getFullYear())
                })
        } else {
            setAttYear(new Date().getFullYear())
            setAnoLetivoAtual(new Date().getFullYear())
        }
        setAttOpen(true)
    }
    const closeAttendance = () => setAttOpen(false)

    // ── modal de boletim ──
    // O modal cuida do próprio fetch/estado; aqui só controlamos abrir/fechar.
    const [rcOpen, setRcOpen] = useState(false)
    const [calOpen, setCalOpen] = useState(false)
    const [ocOpen, setOcOpen] = useState(false)
    const [ocVersao, setOcVersao] = useState(0)
    const ocPend = useOcorrenciasPendentes(studentId, ocVersao)

    // carrega a frequência sempre que o modal abre ou o ano muda
    useEffect(() => {
        if (!attOpen || !studentId) return
        attCancelled.current = false
        setAttLoading(true)

        const load = async () => {
            try {
                const res = await getAttendanceByStudentAndYear(studentId, attYear)
                if (attCancelled.current) return
                setAttRecords(res?.data?.data || [])
            } catch (err) {
                if (attCancelled.current) return
                setAttRecords([])
            } finally {
                if (!attCancelled.current) setAttLoading(false)
            }
        }

        load()
        return () => { attCancelled.current = true }
    }, [attOpen, attYear, studentId])

    const dayMap = buildDayMap(attRecords)
    const totals = attRecords.reduce((acc, rec) => {
        const st = normStatus(rec?.status)
        if (st === 'P') acc.p++
        else if (st === 'F') acc.f++
        else if (st === 'FJ') acc.fj++
        return acc
    }, { p: 0, f: 0, fj: 0 })
    const totalMarcado = totals.p + totals.f + totals.fj
    const freqPct = totalMarcado > 0
        ? Math.round((totals.p / totalMarcado) * 100)
        : null

    useEffect(() => {
        cancelled.current = false

        const load = async () => {
            if (!studentId) {
                setError('Aluno não identificado.')
                setLoading(false)
                return
            }
            setLoading(true)
            setError('')
            try {
                const res = await StdtInfo(studentId)
                if (cancelled.current) return
                // StdtInfo retorna { data: [ {...aluno} ], message }
                // trata array (pega o 1º) ou objeto direto, por robustez
                const payload = res?.data?.data ?? res?.data
                const only = Array.isArray(payload) ? payload[0] : payload
                setStudent(only || null)
            } catch (err) {
                if (cancelled.current) return
                setError('Não foi possível carregar os dados do aluno.')
                setStudent(null)
            } finally {
                if (!cancelled.current) setLoading(false)
            }
        }

        load()
        return () => { cancelled.current = true }
    }, [studentId])

    const goBack = () => navigate('/responsible/students')

    return (
        <Page>
            <TopBar>
                <BackButton onClick={goBack}>
                    <FiArrowLeft size={18} /> Voltar
                </BackButton>
                <TopBarTitle>Painel do Aluno</TopBarTitle>
            </TopBar>

            {loading ? (
                <>
                    <Hero>
                        <HeroInner>
                            <SkeletonBlock $w="88px" $h="88px" $round />
                            <div style={{ flex: 1 }}>
                                <SkeletonLine $w="55%" $h="22px" $light />
                                <SkeletonLine $w="35%" $light />
                            </div>
                        </HeroInner>
                    </Hero>
                    <Content>
                        <Grid>
                            {[1, 2, 3].map(i => (
                                <Card key={i}>
                                    <SkeletonLine $w="40%" $h="15px" />
                                    <SkeletonLine $w="90%" />
                                    <SkeletonLine $w="80%" />
                                    <SkeletonLine $w="85%" />
                                </Card>
                            ))}
                        </Grid>
                    </Content>
                </>
            ) : error ? (
                <Content>
                    <EmptyBox>
                        <EmptyIcon $tone="error"><FiAlertCircle size={26} /></EmptyIcon>
                        {error}
                    </EmptyBox>
                </Content>
            ) : !student ? (
                <Content>
                    <EmptyBox>
                        <EmptyIcon><FiUser size={26} /></EmptyIcon>
                        Aluno não encontrado.
                    </EmptyBox>
                </Content>
            ) : (
                <>
                    <Hero>
                        <HeroInner>
                            {console.log("student", student)}
                            <HeroAvatar>{initials(student.name)}</HeroAvatar>
                            <HeroInfo>
                                <HeroName>{val(student.name)}</HeroName>
                                <HeroSub>
                                    {schoolName(student) || 'Escola não informada'}
                                    {student.registerStudent ? ` • Matrícula ${student.registerStudent}` : ''}
                                </HeroSub>
                                {(() => {
                                    const serie = currentClassSerie(student)
                                    const turma = currentClassName(student)
                                    const regente = regentTeacherName(student)
                                    if (!serie && !turma && !regente) return null
                                    return (
                                        <HeroClassInfo>
                                            {serie && (
                                                <HeroClassLine>
                                                    <FiBookOpen size={14} /> {serie}
                                                </HeroClassLine>
                                            )}
                                            {turma && (
                                                <HeroClassLine>
                                                    <FiUsers size={14} /> {turma}
                                                </HeroClassLine>
                                            )}
                                            {regente && (
                                                <HeroClassLine>
                                                    <FiUser size={14} /> Prof.ª {regente}
                                                </HeroClassLine>
                                            )}
                                        </HeroClassInfo>
                                    )
                                })()}
                                {student.status && (
                                    <StatusBadge $status={student.status}>
                                        {statusLabel(student.status)}
                                    </StatusBadge>
                                )}
                            </HeroInfo>
                        </HeroInner>
                    </Hero>

                    <Content>
                        {(() => {
                            const format = assessmentFormatOf(student)
                            return (
                                <>
                                    <ActionsSection>
                                        <ActionsTitle>Acesso rápido</ActionsTitle>
                                        <ActionGrid>
                                            {/* Sempre visível */}
                                            <ActionButton type="button" onClick={openAttendance}>
                                                <ActionIcon><FiCheckSquare size={22} /></ActionIcon>
                                                <ActionLabel>Frequências</ActionLabel>
                                                <FiChevronRight className="go" size={18} />
                                            </ActionButton>

                                            <ActionButton type="button" onClick={() => setRcOpen(true)}>
                                                <ActionIcon><FiFileText size={22} /></ActionIcon>
                                                <ActionLabel>Boletim</ActionLabel>
                                                <FiChevronRight className="go" size={18} />
                                            </ActionButton>

                                            {/* ocorrências (só visualização + "Estou ciente") */}
                                            <ActionButton type="button" onClick={() => setOcOpen(true)}>
                                                <ActionIcon><FiAlertCircle size={22} /></ActionIcon>
                                                <ActionLabel>
                                                    Ocorrências
                                                    {ocPend.total > 0 && (
                                                        <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 999, background: '#dc2626', color: '#fff', fontSize: 11.5, fontWeight: 800 }}>{ocPend.total}</span>
                                                    )}
                                                </ActionLabel>
                                                <FiChevronRight className="go" size={18} />
                                            </ActionButton>

                                            {/* calendário da escola (só visualização) */}
                                            <ActionButton type="button" onClick={() => setCalOpen(true)}>
                                                <ActionIcon><FiCalendar size={22} /></ActionIcon>
                                                <ActionLabel>Calendário</ActionLabel>
                                                <FiChevronRight className="go" size={18} />
                                            </ActionButton>

                                            {/* Só escola por nota (grade): Avaliações */}
                                            {format === 'grade' && (
                                                <ActionButton type="button" onClick={() => setActOpen(true)}>
                                                    <ActionIcon><FiClipboard size={22} /></ActionIcon>
                                                    <ActionLabel>Avaliações</ActionLabel>
                                                    <FiChevronRight className="go" size={18} />
                                                </ActionButton>
                                            )}

                                            {format === 'concept' && (
                                                <ActionButton type="button" onClick={() => setRepOpen(true)}>
                                                    <ActionIcon><FiBarChart2 size={22} /></ActionIcon>
                                                    <ActionLabel>Relatório Individual</ActionLabel>
                                                    <FiChevronRight className="go" size={18} />
                                                </ActionButton>
                                            )}
                                        </ActionGrid>
                                    </ActionsSection>

                                    {/* aviso de recuperação (período ou final) acima do gráfico */}
                                    {format === 'grade' && <AvisoRecuperacao id_student={studentId} nome={student?.name} />}

                                    {/* ocorrência nova aguardando ciência */}
                                    <AvisoOcorrencias pend={ocPend} onAbrir={() => setOcOpen(true)} />

                                    <PerformanceSection
                                        studentId={studentId}
                                        format={assessmentFormatOf(student)}
                                        idSchool={student?.id_school?._id}
                                    />
                                </>
                            )
                        })()}

                        <Grid>

                        </Grid>
                    </Content>
                </>
            )}
            {/* ── Modal: calendário de frequência ── */}
            {attOpen && (
                <ModalOverlay onClick={closeAttendance}>
                    <ModalBox onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>
                                <FiCalendar size={18} /> Frequência de {(student?.name || '').split(/\s+/)[0]}
                            </ModalTitle>
                            <ModalClose onClick={closeAttendance} aria-label="Fechar">
                                <FiX size={20} />
                            </ModalClose>
                        </ModalHeader>

                        <YearNav>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                                <span style={{ fontWeight: 700 }}>Ano Letivo:</span>
                                <select
                                    value={attYear}
                                    onChange={(e) => setAttYear(Number(e.target.value))}
                                    style={{
                                        padding: '7px 12px',
                                        borderRadius: 8,
                                        border: '1.5px solid var(--line)',
                                        fontSize: 15,
                                        fontWeight: 600,
                                        fontFamily: 'inherit',
                                        color: 'var(--teal-text, var(--teal))',
                                        background: 'var(--surface)',
                                        cursor: 'pointer',
                                        outline: 'none'
                                    }}
                                >
                                    {Array.from({ length: 6 }, (_, i) => anoLetivoAtual - i).map((y) => (
                                        <option key={y} value={y}>
                                            {y}{y === anoLetivoAtual ? ' (atual)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </YearNav>

                        <Legend>
                            <LegendItem><LegendDot $status="P" /> Presença</LegendItem>
                            <LegendItem><LegendDot $status="F" /> Falta</LegendItem>
                            <LegendItem><LegendDot $status="FJ" /> Falta justificada</LegendItem>
                            <LegendItem><LegendDot /> Sem registro</LegendItem>
                        </Legend>

                        <ModalBody>
                            {attLoading ? (
                                <ModalLoading>Carregando frequência…</ModalLoading>
                            ) : (
                                <>
                                    <SummaryRow>
                                        <SummaryCard $tone="P">
                                            <SummaryValue>{totals.p}</SummaryValue>
                                            <SummaryLabel>Presenças</SummaryLabel>
                                        </SummaryCard>
                                        <SummaryCard $tone="F">
                                            <SummaryValue>{totals.f}</SummaryValue>
                                            <SummaryLabel>Faltas</SummaryLabel>
                                        </SummaryCard>
                                        <SummaryCard $tone="FJ">
                                            <SummaryValue>{totals.fj}</SummaryValue>
                                            <SummaryLabel>Faltas justificadas</SummaryLabel>
                                        </SummaryCard>
                                        <SummaryCard $tone="pct">
                                            <SummaryValue>{freqPct == null ? '—' : `${freqPct}%`}</SummaryValue>
                                            <SummaryLabel>Frequência</SummaryLabel>
                                        </SummaryCard>
                                    </SummaryRow>

                                    <MonthsGrid>
                                        {MESES.map((_, i) => (
                                            <MonthCalendar
                                                key={i}
                                                monthIndex={i}
                                                year={attYear}
                                                dayMap={dayMap}
                                                MonthCard={MonthCard}
                                                MonthName={MonthName}
                                                WeekHeader={WeekHeader}
                                                DaysGrid={DaysGrid}
                                                DayCell={DayCell}
                                            />
                                        ))}
                                    </MonthsGrid>
                                </>
                            )}
                        </ModalBody>
                    </ModalBox>
                </ModalOverlay>
            )}

            {/* ── Modal: boletim ── */}
            <ModalOcorrenciasPais
                open={ocOpen}
                onClose={() => setOcOpen(false)}
                id_student={studentId}
                nome={student?.name}
                onMudou={() => setOcVersao(v => v + 1)}
            />
            <CalendarioEscolar
                open={calOpen}
                onClose={() => setCalOpen(false)}
                idSchool={student?.id_school?._id}
                nomeEscola={schoolName(student)}
            />

            <ReportCardModal
                open={rcOpen}
                onClose={() => setRcOpen(false)}
                studentId={studentId}
                studentName={student?.name}
                schoolName={schoolName(student)}
                format={assessmentFormatOf(student)}
            />

            <ActivitiesModal
                open={actOpen}
                onClose={() => setActOpen(false)}
                studentId={studentId}
                studentName={student?.name}
                schoolName={schoolName(student)}
                idSchool={student?.id_school?._id}
            />

            <IndividualReportsModal
                open={repOpen}
                onClose={() => setRepOpen(false)}
                studentId={studentId}
                studentName={student?.name}
                schoolName={schoolName(student)}
                idSchool={student?.id_school?._id}
            />
        </Page>
    )
}

export default ResponsibleStudentDashboard